import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Button, Space, Input, Select, Tag, message, Modal, Form, DatePicker, Drawer, Badge
} from 'antd';
import { PlusOutlined, SearchOutlined, FileTextOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import dayjs from 'dayjs';
import { useHrOptions } from '../../../hooks/useHrOptions';
import EmployeeForm from '../Employee/EmployeeForm';
import { HR_PROCESS_PAGES, PROCESS_STATUS, PROBATION_STATUS } from '../../../config/hrConstants';
import { PageContainer, PageTitle, PageToolbar, DataTable, createPaginationState } from '../../../components/PageLayout';
import ProcessDetailContent from '../../../components/ProcessDetail/ProcessDetailContent';
import ProcessSubmitPreviewModal from '../../../components/ProcessDetail/ProcessSubmitPreviewModal';
import ProcessDraftSubmitAction from '../../../components/ProcessDetail/ProcessDraftSubmitAction';
import ApprovalActions from '../../../components/ProcessDetail/ApprovalActions';
import {
  resolveShowApprovalActions,
  resolveApproveBlockedHint
} from '../../../components/ProcessDetail/approvalActionUtils';
import ProcessCopyAction, { canCopyProcess } from '../../../components/ProcessDetail/ProcessCopyAction';
import { useAuth } from '../../../contexts/AuthContext';
import ResignationApplyFields from '../../../components/Hr/ResignationApplyFields';
import ResignationHandoverModal from '../../../components/Hr/ResignationHandoverModal';

const ProcessManage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const config = HR_PROCESS_PAGES[location.pathname];
  const { employees, loadEmployees, subsidiaries, positions, loadLevels, getDeptsBySubsidiary } = useHrOptions();
  const employeeFormRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(createPaginationState());
  const [search, setSearch] = useState({ keyword: '', status: undefined });
  const [modalVisible, setModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({});
  const [levels, setLevels] = useState([]);
  const [depts, setDepts] = useState([]);
  const [draftCount, setDraftCount] = useState(0);
  const [draftDrawerVisible, setDraftDrawerVisible] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftList, setDraftList] = useState([]);
  const [draftPagination, setDraftPagination] = useState(createPaginationState());
  const [draftDetail, setDraftDetail] = useState(null);
  const [draftDetailVisible, setDraftDetailVisible] = useState(false);
  const [draftActionLoading, setDraftActionLoading] = useState(false);
  const [resignationAttachments, setResignationAttachments] = useState([]);
  const [handoverRecord, setHandoverRecord] = useState(null);

  const fetchList = async () => {
    if (!config) return;
    try {
      setLoading(true);
      const res = await api.get('/hr/processes', {
        params: { process_type: config.type, page: pagination.current, pageSize: pagination.pageSize, ...search }
      });
      if (res.data.success) {
        setData(res.data.data.list);
        setPagination((p) => ({ ...p, total: res.data.data.total }));
      }
    } catch {
      message.error('获取流程列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchDraftCount = useCallback(async () => {
    if (!config) return;
    try {
      const res = await api.get('/approval-center/tasks', {
        params: { tab: 'drafts', process_type: config.type, page: 1, pageSize: 1 }
      });
      if (res.data.success) {
        setDraftCount(res.data.data.total || 0);
      }
    } catch {
      setDraftCount(0);
    }
  }, [config]);

  const fetchDraftList = useCallback(async () => {
    if (!config) return;
    try {
      setDraftLoading(true);
      const res = await api.get('/approval-center/tasks', {
        params: {
          tab: 'drafts',
          process_type: config.type,
          page: draftPagination.current,
          pageSize: draftPagination.pageSize
        }
      });
      if (res.data.success) {
        setDraftList(res.data.data.list);
        setDraftPagination((p) => ({ ...p, total: res.data.data.total }));
        setDraftCount(res.data.data.total || 0);
      }
    } catch {
      message.error('加载草稿列表失败');
    } finally {
      setDraftLoading(false);
    }
  }, [config, draftPagination.current, draftPagination.pageSize]);

  useEffect(() => {
    fetchList();
    if (config?.type !== 'resignation') {
      loadEmployees();
    }
    fetchDraftCount();
    if (config?.type === 'transfer') {
      loadLevels().then(setLevels);
    }
  }, [location.pathname, pagination.current, pagination.pageSize, search]);

  useEffect(() => {
    if (draftDrawerVisible) {
      fetchDraftList();
    }
  }, [draftDrawerVisible, fetchDraftList]);

  const openDetail = async (id) => {
    const res = await api.get(`/hr/processes/${id}`);
    if (res.data.success) {
      setDetail(res.data.data);
      setDetailVisible(true);
    }
  };

  const collectProcessFormData = async (submit, values = {}) => {
    if (config.type === 'onboarding') {
      if (submit) {
        return employeeFormRef.current?.getPayload?.() ?? null;
      }
      return employeeFormRef.current?.getDraftPayload?.() ?? {};
    }
    if (config.type === 'resignation') {
      return {
        real_name: formData.real_name,
        resign_type: values.resign_type,
        reason: values.reason,
        attachment_paths: resignationAttachments
      };
    }
    return formData;
  };

  const buildPayload = async (submit) => {
    const values = submit ? await form.validateFields() : form.getFieldsValue();
    const employeeForm = await collectProcessFormData(submit, values);
    if (submit && config.type === 'onboarding' && !employeeForm) {
      return null;
    }
    if (submit && config.type !== 'onboarding' && !values.employee_id) {
      message.warning('请选择员工');
      return null;
    }
    return {
      process_type: config.type,
      employee_id: values.employee_id,
      effective_date: values.effective_date?.format?.('YYYY-MM-DD') ?? values.effective_date ?? null,
      remark: values.remark,
      form_data: employeeForm || {},
      submit
    };
  };

  const handleSaveDraft = async () => {
    try {
      setDraftSaving(true);
      const payload = await buildPayload(false);
      if (!payload) return;
      await api.post('/hr/processes', payload);
      message.success('草稿已保存');
      setModalVisible(false);
      form.resetFields();
      setFormData({});
      fetchList();
      fetchDraftCount();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || '保存失败');
    } finally {
      setDraftSaving(false);
    }
  };

  const handlePreviewSubmit = async () => {
    try {
      setPreviewLoading(true);
      const payload = await buildPayload(true);
      if (!payload) return;
      const res = await api.post('/hr/processes/preview', payload);
      if (res.data.success) {
        setPendingPayload(payload);
        setPreviewData(res.data.data);
        setPreviewVisible(true);
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || '预览失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    if (!pendingPayload) return;
    try {
      setSubmitLoading(true);
      await api.post('/hr/processes', pendingPayload);
      message.success('流程已提交');
      setPreviewVisible(false);
      setModalVisible(false);
      setPendingPayload(null);
      setPreviewData(null);
      form.resetFields();
      setFormData({});
      setResignationAttachments([]);
      fetchList();
      fetchDraftCount();
    } catch (e) {
      message.error(e.response?.data?.message || '提交失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleActionSuccess = () => {
    setDetailVisible(false);
    fetchList();
  };

  const showApprovalActions = detail && resolveShowApprovalActions(
    detail,
    detail.approve_tasks,
    user?.id,
    detail.canApprove
  );
  const approveBlockedHint = detail && resolveApproveBlockedHint(
    detail,
    detail.approve_tasks,
    user?.id,
    detail.canApprove
  );
  const showCopyProcess = detail && canCopyProcess(detail, user?.id);

  const openCreateModal = () => {
    form.resetFields();
    setFormData({});
    setResignationAttachments([]);
    setModalVisible(true);
  };

  const openDraftDetail = async (record) => {
    try {
      const res = await api.get(`/approval-center/initiated/${record.id}`, {
        params: { biz_type: 'hr_process' }
      });
      if (res.data.success) {
        setDraftDetail(res.data.data);
        setDraftDetailVisible(true);
      }
    } catch {
      message.error('加载草稿详情失败');
    }
  };

  const handleDeleteDraft = (record) => {
    Modal.confirm({
      title: '确认删除草稿？',
      content: '删除后不可恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setDraftActionLoading(true);
          await api.delete(`/approval-center/drafts/${record.id}`, {
            params: { biz_type: 'hr_process' }
          });
          message.success('草稿已删除');
          if (draftDetailVisible && draftDetail?.process?.id === record.id) {
            setDraftDetailVisible(false);
          }
          fetchDraftList();
          fetchDraftCount();
        } catch (e) {
          message.error(e.response?.data?.message || '删除失败');
        } finally {
          setDraftActionLoading(false);
        }
      }
    });
  };

  const handleDraftActionSuccess = () => {
    setDraftDetailVisible(false);
    setDraftDrawerVisible(false);
    fetchDraftList();
    fetchDraftCount();
    fetchList();
  };

  const loadEmployeeInfo = async (employeeId) => {
    if (!employeeId) return;
    const url = config.type === 'resignation'
      ? `/hr/processes/employee-picker/employee/${employeeId}`
      : `/hr/employees/${employeeId}`;
    const params = config.type === 'resignation' ? { process_type: 'resignation' } : undefined;
    const res = await api.get(url, { params });
    if (res.data.success) {
      const emp = res.data.data;
      if (config.type === 'transfer') {
        setFormData({
          from_subsidiary_org_id: emp.subsidiary_org_id,
          from_dept_org_id: emp.dept_org_id,
          from_position_id: emp.position_id,
          real_name: emp.real_name
        });
      }
      if (config.type === 'regularization') {
        setFormData({ regularization_date: emp.regularization_date, probation_status: emp.probation_status, real_name: emp.real_name });
      }
      if (config.type === 'resignation') {
        setFormData((prev) => ({ ...prev, real_name: emp.real_name, employment_status: emp.employment_status }));
      }
    }
  };

  const getDraftTitle = (record) => record.title || record.process_no || '-';

  const draftColumns = [
    { title: '流程编号', dataIndex: 'process_no', width: 150 },
    {
      title: '类型',
      dataIndex: 'process_type_label',
      width: 100,
      render: (v, r) => v || r.process_type
    },
    { title: '标题', dataIndex: 'title', ellipsis: true, render: (_, r) => getDraftTitle(r) },
    {
      title: '状态',
      width: 80,
      render: () => <Tag color="default">草稿</Tag>
    },
    {
      title: '保存时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      width: 130,
      render: (_, r) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openDraftDetail(r)}>查看</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteDraft(r)}>删除</Button>
        </Space>
      )
    }
  ];

  const columns = [
    { title: '流程编号', dataIndex: 'process_no', width: 150 },
    { title: '员工', dataIndex: 'employee_name', width: 100, render: (v, r) => v || (r.form_data?.real_name) || '-' },
    { title: '申请人', dataIndex: 'applicant_name', width: 100 },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (s) => {
        const st = PROCESS_STATUS[s] || { label: s, color: 'default' };
        return <Tag color={st.color}>{st.label}</Tag>;
      }
    },
    { title: '生效日期', dataIndex: 'effective_date', width: 110, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '-' },
    { title: '申请时间', dataIndex: 'create_time', width: 150, render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm') },
    {
      title: '操作',
      key: 'action',
      width: config.type === 'resignation' ? 180 : 100,
      render: (_, r) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openDetail(r.id)}>详情</Button>
          {config.type === 'resignation' && r.status === 'approved' && !r.handover_completed && (
            <Button type="link" size="small" onClick={() => setHandoverRecord(r)}>办理交接</Button>
          )}
        </Space>
      )
    }
  ];

  if (!config) return null;

  const renderProcessForm = () => {
    if (config.type === 'onboarding') {
      return (
        <EmployeeForm
          ref={employeeFormRef}
          employee={null}
          embedded
          onboardingMode
          mobileRequired
          onSuccess={() => {}}
          onCancel={() => {}}
        />
      );
    }
    if (config.type === 'regularization') {
      return (
        <Form layout="vertical">
          <Form.Item label="转正日期"><DatePicker style={{ width: '100%' }} onChange={(d) => setFormData({ ...formData, regularization_date: d?.format('YYYY-MM-DD') })} /></Form.Item>
          <Form.Item label="转正状态"><Select options={PROBATION_STATUS.map((v) => ({ value: v, label: v }))} onChange={(v) => setFormData({ ...formData, probation_status: v })} defaultValue="已转正" /></Form.Item>
        </Form>
      );
    }
    if (config.type === 'transfer') {
      return (
        <Form layout="vertical">
          <Form.Item label="新子公司"><Select options={subsidiaries.map((o) => ({ value: o.id, label: o.org_name }))} onChange={(v) => { setDepts(getDeptsBySubsidiary(v)); setFormData({ ...formData, to_subsidiary_org_id: v }); }} /></Form.Item>
          <Form.Item label="新部门/项目"><Select options={depts.map((o) => ({ value: o.id, label: o.org_name }))} onChange={(v) => setFormData({ ...formData, to_dept_org_id: v })} /></Form.Item>
          <Form.Item label="新岗位"><Select options={positions.map((p) => ({ value: p.id, label: p.label || p.position_name }))} onChange={(v) => setFormData({ ...formData, to_position_id: v })} /></Form.Item>
          <Form.Item label="新职级"><Select options={levels.map((l) => ({ value: l.id, label: l.level_name }))} onChange={(v) => setFormData({ ...formData, to_position_level_id: v })} /></Form.Item>
          <Form.Item label="调岗原因"><Input.TextArea rows={2} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} /></Form.Item>
        </Form>
      );
    }
    return null;
  };

  const modalFooter = (
    <Space>
      <Button onClick={() => setModalVisible(false)}>取消</Button>
      {config.type !== 'resignation' && (
        <Button loading={draftSaving} onClick={handleSaveDraft}>保存草稿</Button>
      )}
      <Button type="primary" loading={previewLoading} onClick={handlePreviewSubmit}>提交审批</Button>
    </Space>
  );

  const draftProcess = draftDetail?.process;

  return (
    <PageContainer>
      <PageTitle>{config.title}</PageTitle>
      <PageToolbar
        filters={(
          <>
            <Input placeholder="流程编号/员工姓名" value={search.keyword} onChange={(e) => setSearch({ ...search, keyword: e.target.value })} style={{ width: 180 }} />
            <Select placeholder="状态" allowClear style={{ width: 120 }} value={search.status} onChange={(v) => setSearch({ ...search, status: v })}
              options={Object.entries(PROCESS_STATUS).filter(([k]) => k !== 'draft').map(([k, v]) => ({ value: k, label: v.label }))} />
            <Button type="primary" icon={<SearchOutlined />} onClick={() => setPagination((p) => ({ ...p, current: 1 }))}>搜索</Button>
          </>
        )}
        actions={(
          <Space>
            {config.type !== 'resignation' && draftCount > 0 && (
              <Badge count={draftCount} size="small" offset={[-4, 4]}>
                <Button icon={<FileTextOutlined />} onClick={() => setDraftDrawerVisible(true)}>草稿</Button>
              </Badge>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>发起申请</Button>
          </Space>
        )}
      />
      <DataTable columns={columns} dataSource={data} rowKey="id" loading={loading} size="small"
        pagination={{ ...pagination, onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize })) }} />

      <Modal
        title={`发起${config.title.replace('管理', '')}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={820}
        footer={modalFooter}
        destroyOnHidden
      >
        {config.type === 'resignation' ? (
          <Form form={form} layout="vertical">
            <ResignationApplyFields
              onEmployeeSelect={loadEmployeeInfo}
              attachments={resignationAttachments}
              onAttachmentsChange={setResignationAttachments}
            />
          </Form>
        ) : config.type !== 'onboarding' && (
          <Form form={form} layout="vertical">
            <Form.Item name="employee_id" label="选择员工" rules={[{ required: true, message: '请选择员工' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                onChange={loadEmployeeInfo}
                options={employees.map((e) => ({ value: e.id, label: `${e.real_name} (${e.employee_no})` }))}
              />
            </Form.Item>
            <Form.Item name="effective_date" label="生效日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="remark" label="备注"><Input.TextArea rows={2} /></Form.Item>
          </Form>
        )}
        {renderProcessForm()}
      </Modal>

      <ProcessSubmitPreviewModal
        open={previewVisible}
        previewProcess={previewData ? {
          ...previewData,
          status: 'draft',
          workflow: previewData.workflow,
          applicant_name: previewData.applicant_name
        } : null}
        confirmLoading={submitLoading}
        cancelText="返回修改"
        onCancel={() => { setPreviewVisible(false); setPendingPayload(null); setPreviewData(null); }}
        onConfirm={handleConfirmSubmit}
      />

      <Drawer
        title={`${config.title.replace('管理', '')}草稿`}
        open={draftDrawerVisible}
        onClose={() => setDraftDrawerVisible(false)}
        width={720}
      >
        <DataTable
          columns={draftColumns}
          dataSource={draftList}
          rowKey="id"
          loading={draftLoading}
          size="small"
          pagination={{
            ...draftPagination,
            onChange: (page, pageSize) => setDraftPagination((p) => ({ ...p, current: page, pageSize }))
          }}
        />
      </Drawer>

      <Drawer
        title={draftProcess ? `草稿详情 - ${draftProcess.process_no}` : '草稿详情'}
        open={draftDetailVisible}
        onClose={() => setDraftDetailVisible(false)}
        width={800}
        styles={{ body: { padding: 0 } }}
      >
        {draftProcess && (
          <>
            <ProcessDetailContent
              process={draftProcess}
              processType={config.type}
              processTypeLabel={config.title}
              bizType="hr_process"
              bizId={draftProcess.id}
              commentApi="approval-center"
            />
            {draftDetail?.canSubmit && (
              <ProcessDraftSubmitAction
                processId={draftProcess.id}
                loading={draftActionLoading}
                onSuccess={handleDraftActionSuccess}
              />
            )}
            {draftDetail?.canDelete && (
              <div className="process-detail-actions">
                <Button danger loading={draftActionLoading} onClick={() => handleDeleteDraft({
                  id: draftProcess.id,
                  biz_type: 'hr_process'
                })}>
                  删除草稿
                </Button>
              </div>
            )}
          </>
        )}
      </Drawer>

      <Drawer
        title={`流程详情 - ${detail?.process_no}`}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        width={800}
        styles={{ body: { padding: 0 } }}
      >
        {detail && (
          <>
            <ProcessDetailContent
              process={detail}
              processType={config.type}
              processTypeLabel={config.title}
              bizType="hr_process"
              bizId={detail.id}
            />
            {detail.status === 'pending' && showApprovalActions && (
              <ApprovalActions
                processId={detail.id}
                apiMode="hr-process"
                approvalActions={detail.approval_actions}
                returnSteps={detail.return_steps || []}
                onSuccess={handleActionSuccess}
              />
            )}
            {detail.status === 'pending' && !showApprovalActions && approveBlockedHint === 'wait_pre_add_sign' && (
              <div className="process-detail-actions process-detail-actions--hint">
                已发起前加签，请等待加签人审批完成后再处理
              </div>
            )}
            {detail.status === 'pending' && !showApprovalActions && approveBlockedHint === 'only_current' && (
              <div className="process-detail-actions process-detail-actions--hint">
                审批请在
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate('/approval-center?tab=todo')}>
                  审批中心
                </Button>
                处理（仅当前审批人可操作）
              </div>
            )}
            {showCopyProcess && (
              <ProcessCopyAction
                process={detail}
                bizType="hr_process"
                onBeforeNavigate={() => setDetailVisible(false)}
              />
            )}
          </>
        )}
      </Drawer>

      {config.type === 'resignation' && (
        <ResignationHandoverModal
          open={Boolean(handoverRecord)}
          processId={handoverRecord?.id}
          employeeName={handoverRecord?.employee_name}
          onCancel={() => setHandoverRecord(null)}
          onSuccess={() => {
            const processId = handoverRecord?.id;
            setHandoverRecord(null);
            fetchList();
            if (detailVisible && processId && detail?.id === processId) {
              openDetail(processId);
            }
          }}
        />
      )}
    </PageContainer>
  );
};

export default ProcessManage;
